import { Copy, RotateCcw } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { useTranslations } from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { colocaToast } from "@/shared/utils/toast";

import {
  ORGANIZATION_WEBHOOK_ENDPOINT_DISABLED_ERROR,
  type OrganizationWebhookDelivery,
} from "../../services/organizationWebhooksService";
import {
  deliveryStatusVariant,
  formatLatency,
  isResendDelivery,
  isRetryableDelivery,
  prettyJson,
} from "../../utils/organizationWebhooks.utils";
import { deliveryStatusLabelKey, eventLabel, eventTypeLabel } from "./webhookLabels";

export interface WebhookDeliveryDrawerProps {
  delivery: OrganizationWebhookDelivery | null;
  open: boolean;
  canWrite: boolean;
  isRetrying: boolean;
  onRetry: (id: string) => void;
  onClose: () => void;
}

/**
 * Everything about one delivery: what was sent (the exact envelope, so the reader can replay it against their
 * own handler) and what came back (truncated to 2 KB by the backend). The retry button honours the backend's
 * allowlist — only a terminal failure can be re-queued — and the current endpoint URL is what a retry goes
 * to.
 */
export function WebhookDeliveryDrawer({
  delivery,
  open,
  canWrite,
  isRetrying,
  onRetry,
  onClose,
}: WebhookDeliveryDrawerProps) {
  const { t } = useTranslations("settings");

  const copy = (value: string) => {
    void navigator.clipboard
      .writeText(value)
      .then(() => colocaToast.success(t("webhooks_copy_success")))
      .catch(() => undefined);
  };

  const retryable = delivery ? isRetryableDelivery(delivery.status) : false;
  // A delivered event is re-SENT, not retried: the receiver gets it again under a new envelope id.
  // Labelling both "Retry" would hide that the second processing is the whole point.
  const isResend = delivery ? isResendDelivery(delivery.status) : false;
  const lastError =
    delivery?.last_error === ORGANIZATION_WEBHOOK_ENDPOINT_DISABLED_ERROR
      ? t("webhooks_endpoint_disabled_error")
      : delivery?.last_error;

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="lg"
      title={t("webhooks_drawer_title")}
      pinLastChild={false}
      footer={
        delivery && canWrite ? (
          <ColocaButton
            variant="primary"
            icon={RotateCcw}
            disabled={!retryable}
            loading={isRetrying}
            onClick={() => onRetry(delivery.id)}
            title={
              retryable
                ? isResend
                  ? t("webhooks_drawer_resend_hint")
                  : undefined
                : t("webhooks_drawer_retry_hint")
            }
          >
            {t(isResend ? "webhooks_drawer_resend" : "webhooks_drawer_retry")}
          </ColocaButton>
        ) : undefined
      }
    >
      {delivery && (
        <>
          {/* Headline */}
          <div className="mb-4 rounded-xl border border-border bg-muted/40 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <ColocaBadge variant="secondary" className="text-[10px]">
                {eventTypeLabel(t, delivery.type)}
              </ColocaBadge>
              <span className="text-[13px] text-foreground">{eventLabel(t, delivery.event)}</span>
              <ColocaStatusPill variant={deliveryStatusVariant(delivery.status)} size="sm" withDot>
                {t(deliveryStatusLabelKey(delivery.status))}
              </ColocaStatusPill>
              {/* The raw value is what the receiver's router matches on, so it is shown verbatim too. */}
              <code className="ml-auto font-mono text-[11px] text-muted-foreground">{delivery.event}</code>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3">
              <Stat label={t("webhooks_th_http")} value={delivery.last_status_code?.toString() ?? "—"} />
              <Stat label={t("webhooks_th_attempts")} value={String(delivery.attempt_count)} />
              <Stat
                label={t("webhooks_th_latency")}
                value={formatLatency(delivery.response_time_ms) ?? "—"}
              />
            </div>
          </div>

          {/* Identity */}
          <div className="mb-4 rounded-xl border border-border bg-card p-4">
            <div className="divide-y divide-border/60">
              <DetailRow
                label={t("webhooks_drawer_id")}
                value={delivery.id}
                onCopy={copy}
                copyAria={t("webhooks_copy_aria")}
              />
              {delivery.event_id && (
                <DetailRow
                  label={t("webhooks_drawer_event_id")}
                  value={delivery.event_id}
                  onCopy={copy}
                  copyAria={t("webhooks_copy_aria")}
                />
              )}
              <DetailRow label={t("webhooks_drawer_url")} value={delivery.url} />
              <DetailRow label={t("webhooks_drawer_created")} value={formatDateTime(delivery.created_at)} />
              {delivery.delivered_at && (
                <DetailRow
                  label={t("webhooks_drawer_delivered")}
                  value={formatDateTime(delivery.delivered_at)}
                />
              )}
              {lastError && (
                <DetailRow label={t("webhooks_drawer_last_error")} value={lastError} tone="danger" />
              )}
            </div>
          </div>

          {/* Payload */}
          <CodeBlock
            label={t("webhooks_drawer_payload")}
            code={prettyJson(delivery.payload)}
            onCopy={copy}
            copyAria={t("webhooks_copy_aria")}
          />

          {/* Response */}
          <CodeBlock
            label={t("webhooks_drawer_response")}
            code={delivery.response_body ? prettyJson(delivery.response_body) : ""}
            emptyText={t("webhooks_drawer_response_empty")}
            footnote={t("webhooks_drawer_response_truncated")}
            onCopy={copy}
            copyAria={t("webhooks_copy_aria")}
          />
        </>
      )}
    </ColocaDrawer>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-mono text-[14px] tabular-nums text-foreground">{value}</div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  tone,
  onCopy,
  copyAria,
}: {
  label: string;
  value: string;
  tone?: "danger";
  onCopy?: (value: string) => void;
  copyAria?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="inline-flex min-w-0 items-center gap-1">
        <span
          className={
            tone === "danger"
              ? "truncate font-mono text-xs text-destructive"
              : "truncate font-mono text-xs text-foreground"
          }
          title={value}
        >
          {value}
        </span>
        {onCopy && copyAria && (
          <ColocaIconButton
            icon={Copy}
            variant="ghost"
            size="sm"
            aria-label={copyAria}
            onClick={() => onCopy(value)}
          />
        )}
      </span>
    </div>
  );
}

function CodeBlock({
  label,
  code,
  emptyText,
  footnote,
  onCopy,
  copyAria,
}: {
  label: string;
  code: string;
  emptyText?: string;
  footnote?: string;
  onCopy: (value: string) => void;
  copyAria: string;
}) {
  return (
    <div className="mb-4 rounded-xl border border-border bg-card p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
        {code && (
          <ColocaIconButton
            icon={Copy}
            variant="ghost"
            size="sm"
            aria-label={copyAria}
            onClick={() => onCopy(code)}
          />
        )}
      </div>
      {code ? (
        <pre className="max-h-[320px] overflow-auto rounded-md bg-muted/50 p-3 font-mono text-[11.5px] leading-relaxed text-foreground">
          {code}
        </pre>
      ) : (
        <p className="text-[12px] text-muted-foreground">{emptyText}</p>
      )}
      {footnote && <p className="mt-2 text-[11px] text-muted-foreground">{footnote}</p>}
    </div>
  );
}
